import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import './App.css';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

function InvoiceGen() {
  const invoiceGenStructuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "InvoiceGen",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "INR"
    },
    "description": "Free GST Invoice Generator - Create professional GST-compliant invoices instantly. No signup required.",
    "featureList": ["GST Compliant Invoices", "PDF Download", "Multiple Tax Rates", "HSN/SAC Codes", "Professional Templates"],
    "screenshot": "https://bramhaware.in/invoicegenlogo.png",
    "softwareVersion": "1.0",
    "author": {
      "@type": "Organization",
      "name": "BRAMHAWARE"
    }
  };
  const [gstMode, setGstMode] = useState('individual'); // 'individual' or 'all'
  const [gstAll, setGstAll] = useState(18);

  // Column configuration
  const [columnConfig, setColumnConfig] = useState({
    srNo: { enabled: true, fixed: true, label: 'Sr. No', width: '4%' },
    description: { enabled: true, fixed: true, label: 'Item Description', width: '20%' },
    hsn: { enabled: true, fixed: false, label: 'HSN/SAC', width: '10%' },
    quantity: { enabled: true, fixed: false, label: 'Qty', width: '6%' },
    unit: { enabled: true, fixed: false, label: 'Unit', width: '8%' },
    rate: { enabled: true, fixed: true, label: 'Rate', width: '10%' },
    amount: { enabled: true, fixed: false, label: 'Amount', width: '10%' },
    gstPercent: { enabled: true, fixed: false, label: 'GST %', width: '6%' },
    gstAmount: { enabled: true, fixed: false, label: 'GST Amt', width: '6%' },
    itemTotal: { enabled: true, fixed: false, label: 'Item Total', width: '10%' }
  });

  // Available columns that can be added
  const [availableColumns] = useState({
    discount: { label: 'Discount', width: '8%' },
    tax: { label: 'Tax', width: '6%' },
    total: { label: 'Total', width: '10%' },
    remarks: { label: 'Remarks', width: '12%' }
  });

  // Clean up any invalid columns that might have been added
  useEffect(() => {
    const validColumns = ['srNo', 'description', 'hsn', 'quantity', 'unit', 'rate', 'amount', 'gstPercent', 'gstAmount', 'itemTotal', ...Object.keys(availableColumns)];
    setColumnConfig(prev => {
      const cleaned = {};
      Object.entries(prev).forEach(([key, value]) => {
        if (validColumns.includes(key)) {
          cleaned[key] = value;
        }
      });
      return cleaned;
    });
  }, [availableColumns]);

  // Enhanced invoice structure
  const [invoice, setInvoice] = useState({
    // Company/Seller Details
    companyName: '',
    companyAddress: '',
    companyCity: '',
    companyState: '',
    companyPincode: '',
    companyPhone: '',
    companyEmail: '',
    companyGST: '',
    
    // Customer/Buyer Details
    customerName: '',
    customerAddress: '',
    customerCity: '',
    customerState: '',
    customerPincode: '',
    customerPhone: '',
    customerGST: '',
    
    // Invoice Details
    invoiceNumber: '',
    invoiceDate: '',
    dueDate: '',
    placeOfSupply: '',
    
    items: [
      { description: '', hsn: '', quantity: 1, unit: 'Nos', rate: '', gst: 18, amount: 0 }
    ]
  });

  const previewRef = useRef(null);

  const handleChange = e => {
    setInvoice({ ...invoice, [e.target.name]: e.target.value });
  };

  const handleItemChange = (idx, e) => {
    const items = invoice.items.map((item, i) => {
      if (i === idx) {
        const updatedItem = { ...item, [e.target.name]: e.target.value };
        // Auto-calculate amount when quantity or rate changes
        if (e.target.name === 'quantity' || e.target.name === 'rate') {
          updatedItem.amount = Number(updatedItem.quantity) * Number(updatedItem.rate);
        }
        return updatedItem;
      }
      return item;
    });
    setInvoice({ ...invoice, items });
  };

  const addItem = () => {
    setInvoice({
      ...invoice,
      items: [...invoice.items, { description: '', hsn: '', quantity: 1, unit: 'Nos', rate: '', gst: 18, amount: 0 }]
    });
  };

  const removeItem = idx => {
    setInvoice({
      ...invoice,
      items: invoice.items.filter((_, i) => i !== idx)
    });
  };

  // Column management functions
  const toggleColumn = (columnKey) => {
    if (columnConfig[columnKey].fixed) return; // Can't toggle fixed columns
    setColumnConfig(prev => ({
      ...prev,
      [columnKey]: {
        ...prev[columnKey],
        enabled: !prev[columnKey].enabled
      }
    }));
  };

  const addColumn = (columnKey) => {
    if (columnConfig[columnKey]) return; // Already exists
    setColumnConfig(prev => ({
      ...prev,
      [columnKey]: {
        ...availableColumns[columnKey],
        enabled: true,
        fixed: false
      }
    }));
  };

  const getEnabledColumns = () => {
    return Object.entries(columnConfig)
      .filter(([_, config]) => config.enabled)
      .map(([key, config]) => ({ key, ...config }));
  };



  const renderFormField = (column, item, idx) => {
    switch (column.key) {
      case 'srNo':
        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontWeight: 'bold',
            color: '#3498db',
            backgroundColor: '#f0f8ff',
            borderRadius: '6px',
            height: '32px',
            fontSize: '0.9rem'
          }}>
            {idx + 1}
          </div>
        );
      case 'description':
        return (
          <input
            name="description"
            aria-label={`${column.label}, item ${idx + 1}`}
            placeholder="Item Description"
            value={item.description}
            onChange={e => handleItemChange(idx, e)}
          />
        );
      case 'hsn':
        return (
          <input
            name="hsn"
            aria-label={`${column.label}, item ${idx + 1}`}
            placeholder="HSN/SAC"
            value={item.hsn}
            onChange={e => handleItemChange(idx, e)}
          />
        );
      case 'quantity':
        return (
          <input
            name="quantity"
            aria-label={`${column.label}, item ${idx + 1}`}
            type="number"
            min="1"
            value={item.quantity}
            onChange={e => handleItemChange(idx, e)}
            placeholder="Qty"
          />
        );
      case 'unit':
        return (
          <select
            name="unit"
            aria-label={`${column.label}, item ${idx + 1}`}
            value={item.unit}
            onChange={e => handleItemChange(idx, e)}
          >
            <option value="Nos">Nos</option>
            <option value="Kg">Kg</option>
            <option value="Ltr">Ltr</option>
            <option value="Mtr">Mtr</option>
            <option value="Box">Box</option>
            <option value="Set">Set</option>
          </select>
        );
      case 'rate':
        return (
          <input
            name="rate"
            aria-label={`${column.label}, item ${idx + 1}`}
            type="number"
            min="0"
            step="0.01"
            value={item.rate}
            onChange={e => handleItemChange(idx, e)}
            placeholder="Rate"
          />
        );
      case 'amount':
        return (
          <input
            name="amount"
            aria-label={`${column.label}, item ${idx + 1}`}
            type="number"
            value={item.amount}
            readOnly
          />
        );
      case 'gstPercent':
        return (
          <input
            name="gst"
            aria-label={`${column.label}, item ${idx + 1}`}
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={gstMode === 'individual' ? item.gst : gstAll}
            onChange={gstMode === 'individual' ? e => handleItemChange(idx, e) : undefined}
            placeholder="GST %"
            readOnly={gstMode === 'all'}
          />
        );
      case 'gstAmount':
        const calculatedGstAmount = ((Number(item.quantity) || 0) * (Number(item.rate) || 0)) * (gstMode === 'all' ? Number(gstAll) : Number(item.gst) || 0) / 100;
        return (
          <input
            name="gstAmount"
            aria-label={`${column.label}, item ${idx + 1}`}
            type="number"
            value={calculatedGstAmount.toFixed(2)}
            readOnly
          />
        );
      case 'itemTotal':
        const calculatedItemTotal = ((Number(item.quantity) || 0) * (Number(item.rate) || 0)) * (1 + (gstMode === 'all' ? Number(gstAll) : Number(item.gst) || 0) / 100);
        return (
          <input
            name="itemTotal"
            aria-label={`${column.label}, item ${idx + 1}`}
            type="number"
            value={calculatedItemTotal.toFixed(2)}
            readOnly
          />
        );
      default:
        return (
          <input
            name={column.key}
            aria-label={`${column.label}, item ${idx + 1}`}
            placeholder={column.label}
            value={item[column.key] || ''}
            onChange={e => handleItemChange(idx, e)}
          />
        );
    }
  };

  const renderTableCell = (column, item, globalIndex) => {
    switch (column.key) {
      case 'srNo':
        return <td key={column.key}>{item.description ? globalIndex : ''}</td>;
      case 'description':
        return <td key={column.key}>{item.description}</td>;
      case 'hsn':
        return <td key={column.key}>{item.hsn}</td>;
      case 'quantity':
        return <td key={column.key}>{item.quantity ? Number(item.quantity) : ''}</td>;
      case 'unit':
        return <td key={column.key}>{item.unit}</td>;
      case 'rate':
        return <td key={column.key}>{item.rate ? `₹${Number(item.rate).toFixed(2)}` : ''}</td>;
      case 'amount':
        return <td key={column.key}>{item.amount ? `₹${Number(item.amount).toFixed(2)}` : ''}</td>;
      case 'gstPercent':
        const displayGstPercent = gstMode === 'individual' ? item.gstPercent : gstAll;
        return <td key={column.key}>{displayGstPercent ? `${Number(displayGstPercent).toFixed(1)}%` : ''}</td>;
      case 'gstAmount':
        return <td key={column.key}>{item.gstAmount ? `₹${Number(item.gstAmount).toFixed(2)}` : ''}</td>;
      case 'itemTotal':
        return <td key={column.key}>{item.totalWithGst ? `₹${Number(item.totalWithGst).toFixed(2)}` : ''}</td>;
      default:
        return <td key={column.key}>{item[column.key] || ''}</td>;
    }
  };

  // Calculate totals and GST
  const itemsWithCalculations = invoice.items.map(item => {
    const quantity = Number(item.quantity) || 0;
    const rate = Number(item.rate) || 0;
    const gstPercent = gstMode === 'all' ? Number(gstAll) : Number(item.gst) || 0;
    const amount = quantity * rate;
    const gstAmount = amount * gstPercent / 100;
    const totalWithGst = amount + gstAmount;
    return { ...item, quantity, rate, gstPercent, amount, gstAmount, totalWithGst };
  });

  const subtotal = itemsWithCalculations.reduce((sum, item) => sum + item.amount, 0);
  const totalGstAmount = itemsWithCalculations.reduce((sum, item) => sum + item.gstAmount, 0);
  const grandTotal = itemsWithCalculations.reduce((sum, item) => sum + item.totalWithGst, 0);

  // Download as PDF
  const handleDownloadPDF = async () => {
    const input = previewRef.current;
    if (!input) return;
    // Add a class to optimize for PDF generation
    input.classList.add('pdf-generation');
    // Wait a tick to ensure class is applied
    await new Promise(r => setTimeout(r, 10));
    const pdf = new jsPDF('p', 'pt', 'a4');

    // Get all invoice pages
    const invoicePages = input.querySelectorAll('.invoice-page');
    for (let i = 0; i < invoicePages.length; i++) {
      const page = invoicePages[i];
      // Capture each page separately with proper scaling for A4
      const canvas = await html2canvas(page, { 
        scale: 1.5, // Higher scale for better quality
        useCORS: true,
        allowTaint: true,
        height: page.scrollHeight, // Use actual content height
        width: page.scrollWidth,   // Use actual content width
        scrollX: 0,
        scrollY: 0,
        backgroundColor: '#ffffff'
      });
      const imgData = canvas.toDataURL('image/png');
      // Calculate proper dimensions to fit A4 page
      const imgProps = pdf.getImageProperties(imgData);
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      // Scale image to fit page while maintaining aspect ratio
      const imgWidth = pageWidth;
      const imgHeight = (imgProps.height * pageWidth) / imgProps.width;
      // If height exceeds page, scale down
      const finalWidth = imgHeight > pageHeight ? (imgProps.width * pageHeight) / imgProps.height : imgWidth;
      const finalHeight = imgHeight > pageHeight ? pageHeight : imgHeight;
      // Add new page for subsequent pages
      if (i > 0) {
        pdf.addPage();
      }
      // Center the image on the page
      const xOffset = (pageWidth - finalWidth) / 2;
      const yOffset = (pageHeight - finalHeight) / 2;
      pdf.addImage(imgData, 'PNG', xOffset, yOffset, finalWidth, finalHeight);
    }
    // Remove the PDF class
    input.classList.remove('pdf-generation');
    pdf.save('invoice.pdf');
  };

  // Download as Word (doc)
  const handleDownloadWord = () => {
    const input = previewRef.current;
    if (!input) return;
    const html = input.outerHTML;
    const blob = new Blob(
      [
        '<html><head><meta charset="utf-8"></head><body>',
        html,
        '</body></html>',
      ],
      { type: 'application/msword' }
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'invoice.doc';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Download as Excel (CSV)
  const handleDownloadExcel = () => {
    const rows = [
      [
        '#', 'Description', 'HSN/SAC', 'Qty', 'Unit', 'Rate', 'Amount', 'GST %', 'GST Amt', 'Total'
      ],
      ...itemsWithCalculations.map((item, idx) => [
        idx + 1,
        item.description,
        item.hsn,
        item.quantity,
        item.unit,
        Number(item.rate).toFixed(2),
        Number(item.amount).toFixed(2),
        Number(item.gstPercent).toFixed(2),
        Number(item.gstAmount).toFixed(2),
        Number(item.totalWithGst).toFixed(2)
      ])
    ];
    const csv = rows.map(r => r.map(String).map(v => `"${v.replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'invoice.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="invoice-app">
      <SEO
        title="InvoiceGen - Free GST Invoice Generator | BRAMHAWARE"
        description="Create professional GST-compliant invoices instantly with InvoiceGen. Free online invoice generator with PDF download, multiple tax rates, HSN/SAC codes support. No signup required."
        keywords="GST Invoice Generator, Free Invoice Maker, GST Billing Software, Invoice PDF, HSN Code, SAC Code, Tax Invoice, India GST, BRAMHAWARE InvoiceGen"
        canonicalUrl="/invoicegen"
        ogType="website"
        ogImage="/invoicegenlogo.png"
        structuredData={invoiceGenStructuredData}
      />
      <div className="app-header">
        <Link to="/" className="back-button">← Back to BRAMHAWARE</Link>
        <h1>InvoiceGen</h1>
        <img src="/invoicegenlogo.png" alt="InvoiceGen Logo" className="app-logo" />
      </div>
      
      <div className="invoice-form">
        <div className="form-section">
          <h3>Company Details</h3>
          <div className="form-row">
            <label>Company Name:<br />
              <input name="companyName" value={invoice.companyName} onChange={handleChange} placeholder="Your Company Name" />
            </label>
            <label>GST Number:<br />
              <input name="companyGST" value={invoice.companyGST} onChange={handleChange} placeholder="GSTIN" />
            </label>
          </div>
          <div className="form-row">
            <label>Address:<br />
              <input name="companyAddress" value={invoice.companyAddress} onChange={handleChange} placeholder="Street Address" />
            </label>
            <label>City:<br />
              <input name="companyCity" value={invoice.companyCity} onChange={handleChange} placeholder="City" />
            </label>
          </div>
          <div className="form-row">
            <label>State:<br />
              <input name="companyState" value={invoice.companyState} onChange={handleChange} placeholder="State" />
            </label>
            <label>PIN Code:<br />
              <input name="companyPincode" value={invoice.companyPincode} onChange={handleChange} placeholder="PIN Code" />
            </label>
          </div>
          <div className="form-row">
            <label>Phone:<br />
              <input name="companyPhone" value={invoice.companyPhone} onChange={handleChange} placeholder="Phone Number" />
            </label>
            <label>Email:<br />
              <input name="companyEmail" value={invoice.companyEmail} onChange={handleChange} placeholder="Email Address" />
            </label>
          </div>
        </div>

        <div className="form-section">
          <h3>Customer Details</h3>
          <div className="form-row">
            <label>Customer Name:<br />
              <input name="customerName" value={invoice.customerName} onChange={handleChange} placeholder="Customer Name" />
            </label>
            <label>Customer GST:<br />
              <input name="customerGST" value={invoice.customerGST} onChange={handleChange} placeholder="Customer GSTIN (Optional)" />
            </label>
          </div>
          <div className="form-row">
            <label>Address:<br />
              <input name="customerAddress" value={invoice.customerAddress} onChange={handleChange} placeholder="Customer Address" />
            </label>
            <label>City:<br />
              <input name="customerCity" value={invoice.customerCity} onChange={handleChange} placeholder="City" />
            </label>
          </div>
          <div className="form-row">
            <label>State:<br />
              <input name="customerState" value={invoice.customerState} onChange={handleChange} placeholder="State" />
            </label>
            <label>PIN Code:<br />
              <input name="customerPincode" value={invoice.customerPincode} onChange={handleChange} placeholder="PIN Code" />
            </label>
          </div>
          <label>Phone:<br />
            <input name="customerPhone" value={invoice.customerPhone} onChange={handleChange} placeholder="Phone Number" />
          </label>
        </div>

        <div className="form-section">
          <h3>Invoice Details</h3>
          <div className="form-row">
            <label>Invoice Number:<br />
              <input name="invoiceNumber" value={invoice.invoiceNumber} onChange={handleChange} placeholder="INV-001" />
            </label>
            <label>Invoice Date:<br />
              <input name="invoiceDate" type="date" value={invoice.invoiceDate} onChange={handleChange} />
            </label>
          </div>
          <div className="form-row">
            <label>Due Date:<br />
              <input name="dueDate" type="date" value={invoice.dueDate} onChange={handleChange} />
            </label>
            <label>Place of Supply:<br />
              <input name="placeOfSupply" value={invoice.placeOfSupply} onChange={handleChange} placeholder="State Name" />
            </label>
          </div>
        </div>

        <div className="form-section items-container">
          <div className="items-header">
            <h3>Items</h3>
          </div>
          
          {/* GST Mode Selection */}
          <div className="gst-mode-selection">
            <label>
              <input
                type="radio"
                name="gstMode"
                value="individual"
                checked={gstMode === 'individual'}
                onChange={() => setGstMode('individual')}
              />
              Individual GST % for each item
            </label>
            <label>
              <input
                type="radio"
                name="gstMode"
                value="all"
                checked={gstMode === 'all'}
                onChange={() => setGstMode('all')}
              />
              Same GST % for all items
            </label>
            {gstMode === 'all' && (
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={gstAll}
                onChange={e => setGstAll(e.target.value)}
                placeholder="GST %"
              />
            )}
          </div>
          
          {/* Column Management */}
          <div className="column-management">
            <h4>Column Settings</h4>
            
            {/* Current Columns */}
            <div style={{ marginBottom: '1rem' }}>
              <strong style={{ fontSize: '0.9rem', marginBottom: '0.5rem', display: 'block' }}>Columns:</strong>
              <div className="column-toggles">
                {Object.entries(columnConfig).map(([key, config]) => (
                  <label key={key} className={`column-toggle ${config.enabled ? 'enabled' : ''} ${config.fixed ? 'fixed' : ''}`}>
                    <input
                      type="checkbox"
                      checked={config.enabled}
                      onChange={() => toggleColumn(key)}
                      disabled={config.fixed}
                      style={{ margin: 0 }}
                    />
                    <span>
                      {config.label}
                    </span>
                    {config.fixed && <span style={{ color: '#999', fontSize: '0.75rem' }}>(fixed)</span>}
                  </label>
                ))}
              </div>
            </div>

            {/* Add New Columns */}
            <div>
              <strong style={{ fontSize: '0.9rem', marginBottom: '0.5rem', display: 'block' }}>Add Column:</strong>
              <div className="add-column-buttons">
                {Object.entries(availableColumns)
                  .filter(([key]) => !columnConfig[key])
                  .map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => addColumn(key)}
                      className="add-column-btn"
                    >
                      + {config.label}
                    </button>
                  ))}
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="items-table-scroll">
          <table className="items-table">
            <thead className="items-table-header">
              <tr>
                {getEnabledColumns().map(column => (
                  <th key={column.key}>
                    {column.label}
                  </th>
                ))}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, idx) => (
                <tr key={idx}>
                  {getEnabledColumns().map(column => (
                    <td key={column.key}>
                      {renderFormField(column, item, idx)}
                    </td>
                  ))}
                  <td>
                    <button 
                      type="button" 
                      className="btn-danger" 
                      onClick={() => removeItem(idx)} 
                      disabled={invoice.items.length === 1}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          
          <div className="action-buttons">
            <button type="button" className="btn-primary" onClick={addItem}>Add Item</button>
          </div>
        </div>
      </div>

      {/* Download buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        marginBottom: '2rem', 
        justifyContent: 'center',
        flexWrap: 'wrap',
        padding: '0 2rem'
      }}>
        <button className="btn-primary" onClick={handleDownloadPDF}>Download PDF</button>
        <button className="btn-primary" onClick={handleDownloadWord}>Download Word</button>
        <button className="btn-primary" onClick={handleDownloadExcel}>Download Excel</button>
      </div>

      {/* Web page summary - always visible at bottom */}
      <div className="invoice-total-bar" role="status" aria-live="polite">
        <span>Grand Total:</span>
        <span className="invoice-total-value">₹{Number(grandTotal).toFixed(2)}</span>
      </div>

      <div className="invoice-preview" ref={previewRef}>
        {/* Calculate pages needed */}
        {(() => {
          const ITEMS_PER_PAGE = 12;
          const totalPages = Math.max(1, Math.ceil(itemsWithCalculations.length / ITEMS_PER_PAGE));
          
          return Array.from({ length: totalPages }, (_, pageIndex) => {
            const startIndex = pageIndex * ITEMS_PER_PAGE;
            const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, itemsWithCalculations.length);
            const pageItems = itemsWithCalculations.slice(startIndex, endIndex);
            
            // Fill remaining rows with empty data to reach 21 items
            const emptyRowsCount = ITEMS_PER_PAGE - pageItems.length;
            const emptyRows = Array.from({ length: emptyRowsCount }, (_, idx) => ({
              description: '',
              hsn: '',
              quantity: '',
              unit: '',
              rate: '',
              amount: '',
              gstPercent: '',
              gstAmount: ''
            }));
            
            const allRowsForPage = [...pageItems, ...emptyRows];
            
            // Calculate page totals for current page items only
            const pageSubtotal = pageItems.reduce((sum, item) => sum + (item.amount || 0), 0);
            const pageTotalGst = pageItems.reduce((sum, item) => sum + (item.gstAmount || 0), 0);
            const pageGrandTotal = pageItems.reduce((sum, item) => sum + (item.totalWithGst || 0), 0);
            
            return (
              <div key={pageIndex} className={`invoice-page ${pageIndex > 0 ? 'page-break' : ''}`}>
                <div className="invoice-content-area">
                  {/* Header section - only on first page */}
                  {pageIndex === 0 && (
                    <>
                      <div className="invoice-header-section">
                        <div className="invoice-title-row">
                          <h2>TAX INVOICE</h2>
                          <img src="/brahmawarequotelogoblue.PNG" alt="BRAMHAWARE Logo" className={`invoice-logo${previewRef.current && previewRef.current.classList.contains('pdf-generation') ? ' pdf-logo' : ''}`} />
                        </div>
                        <div className="invoice-header-details">
                          <div className="left-section">
                            <h3>{invoice.companyName || 'Your Company Name'}</h3>
                            <p>{invoice.companyAddress}</p>
                            <p>{invoice.companyCity}, {invoice.companyState} - {invoice.companyPincode}</p>
                            <p>Phone: {invoice.companyPhone}</p>
                            <p>Email: {invoice.companyEmail}</p>
                            <p><strong>GSTIN: {invoice.companyGST}</strong></p>
                          </div>
                          <div className="right-section">
                            <p><strong>Invoice No:</strong> {invoice.invoiceNumber}</p>
                            <p><strong>Invoice Date:</strong> {invoice.invoiceDate}</p>
                            <p><strong>Due Date:</strong> {invoice.dueDate}</p>
                            <p><strong>Place of Supply:</strong> {invoice.placeOfSupply}</p>
                          </div>
                        </div>
                      </div>

                      <div className="billing-section">
                        <div className="bill-to">
                          <h4>Bill To:</h4>
                          <p><strong>{invoice.customerName}</strong></p>
                          <p>{invoice.customerAddress}</p>
                          <p>{invoice.customerCity}, {invoice.customerState} - {invoice.customerPincode}</p>
                          <p>Phone: {invoice.customerPhone}</p>
                          {invoice.customerGST && <p><strong>GSTIN: {invoice.customerGST}</strong></p>}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Page header for continuation pages */}
                  {pageIndex > 0 && (
                    <div className="continuation-header">
                      <h3>TAX INVOICE (Continued) - Page {pageIndex + 1}</h3>
                      <p><strong>Invoice No:</strong> {invoice.invoiceNumber}</p>
                    </div>
                  )}

                  <table className="invoice-table">
                    <thead>
                      <tr>
                        {getEnabledColumns().map(column => (
                          <th key={column.key} style={{width: column.width}}>
                            {column.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allRowsForPage.map((item, idx) => {
                        const globalIndex = startIndex + idx + 1;
                        return (
                          <tr key={idx}>
                            {getEnabledColumns().map(column => renderTableCell(column, item, globalIndex))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Page Summary - for pages with content but not the last page */}
                {pageIndex < totalPages - 1 && pageItems.length > 0 && (
                  <div className="invoice-page-summary">
                    <div className="page-summary">
                      <div className="summary-row">
                        <span>Page Subtotal:</span>
                        <span>₹{Number(pageSubtotal).toFixed(2)}</span>
                      </div>
                      <div className="summary-row">
                        <span>Page GST:</span>
                        <span>₹{Number(pageTotalGst).toFixed(2)}</span>
                      </div>
                      <div className="summary-row">
                        <span><strong>Page Total:</strong></span>
                        <span><strong>₹{Number(pageGrandTotal).toFixed(2)}</strong></span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Footer section - only on last page */}
                {pageIndex === totalPages - 1 && (
                  <div className="invoice-bottom-section">
                    <div className="invoice-summary">
                      <div className="summary-row">
                        <span>Subtotal:</span>
                        <span>₹{Number(subtotal).toFixed(2)}</span>
                      </div>
                      <div className="summary-row">
                        <span>Total GST:</span>
                        <span>₹{Number(totalGstAmount).toFixed(2)}</span>
                      </div>
                      <div className="summary-row total-row">
                        <span><strong>Grand Total:</strong></span>
                        <span><strong>₹{Number(grandTotal).toFixed(2)}</strong></span>
                      </div>
                    </div>

                    <div className="invoice-footer">
                      <p><strong>Terms & Conditions:</strong></p>
                      <p>1. Payment is due within 30 days of invoice date.</p>
                      <p>2. Please quote invoice number with all correspondence.</p>
                      <p>3. All disputes subject to local jurisdiction.</p>
                    </div>
                  </div>
                )}
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
}

export default InvoiceGen;
